import type { Ref } from 'react'

import { Building2, Truck } from 'lucide-react'

import FilterSelect from '@/components/FilterSelect'
import type { DeliveryDto } from '@/services/deliveryService'

import type { DeliveryView } from './DeliveryViewToggle'
import DeliveryViewToggle from './DeliveryViewToggle'
import PaperworkSearch from './PaperworkSearch'

import './DeliveryFilterBar.css'

/** What the strip can narrow the board by. Absent means the filter is off. */
export type DeliveryFilters = {
  carrier?: string
  vendor?: string
  /**
   * A BOL or PO number, matching one delivery rather than a category. Stored
   * as the number itself, not a delivery id, so a shared link says what it
   * shows — the same reason carrier stores the carrier's name.
   */
  ref?: string
}

type Props = {
  view: DeliveryView
  onViewChange: (view: DeliveryView) => void
  counts: { today: number; week: number; month: number; previous: number }
  filters: DeliveryFilters
  /**
   * The values on offer, sorted, derived from the whole board rather than
   * from what the current view shows — see Deliveries.tsx.
   */
  carriers: readonly string[]
  vendors: readonly string[]
  /**
   * The board, for the paperwork lookup to match against -- everything
   * except terminal history from a previous day, which no view ever renders
   * (see Deliveries.tsx's searchableDeliveries). It takes rows rather than a
   * list of values because a reference number identifies nothing on its
   * own — its result rows carry the carrier and the slot too.
   */
  deliveries: readonly DeliveryDto[]
  /** The page's shared clock, for those rows' Today / Tomorrow labels. */
  now: Date
  onFilterChange: (
    key: keyof DeliveryFilters,
    value: string | undefined,
  ) => void
  /**
   * The sticky strip. The page measures it and republishes the height as a
   * CSS variable, so the day headings pin directly below it however it wraps.
   * A callback ref, since the strip mounts after the page does — see
   * useMeasuredHeight.
   */
  stripRef?: Ref<HTMLDivElement>
}

/**
 * The board's controls: the view toggle, plus the filters that narrow the
 * list.
 *
 * Three filters live in the strip itself rather than behind a Filters button
 * — carrier, vendor, and one box matching either paperwork number. At three,
 * a button plus a panel is more machinery than the job needs: always-visible
 * controls show their own values, which is what applied-filter chips would
 * have existed to do.
 *
 * The strip is sticky, so the controls stay reachable through a week of
 * scrolling. Its height is measured rather than tokenised because the
 * controls wrap onto a second line at narrow widths, and the day headings
 * pin below whatever that height turns out to be.
 */
function DeliveryFilterBar({
  view,
  onViewChange,
  counts,
  filters,
  carriers,
  vendors,
  deliveries,
  now,
  onFilterChange,
  stripRef,
}: Props) {
  return (
    <div className="delivery-filters" ref={stripRef}>
      <DeliveryViewToggle view={view} onChange={onViewChange} counts={counts} />

      <div className="delivery-filters__controls">
        {/* Leads the group: it is the one control someone arrives at the strip
            already knowing what they want from, paperwork in hand. */}
        <PaperworkSearch
          value={filters.ref}
          onChange={(value) => onFilterChange('ref', value)}
          deliveries={deliveries}
          now={now}
        />

        {/* Icons match the ones the tile uses for the same field, so a
            control and the line it narrows read as one subject. */}
        <FilterSelect
          icon={Truck}
          label="Carrier"
          placeholder="All carriers"
          searchPlaceholder="Search carriers…"
          emptyText="No carrier on the board matches."
          options={carriers}
          value={filters.carrier}
          onChange={(value) => onFilterChange('carrier', value)}
        />

        <FilterSelect
          icon={Building2}
          label="Vendor"
          placeholder="All vendors"
          searchPlaceholder="Search vendors…"
          emptyText="No vendor on the board matches."
          options={vendors}
          value={filters.vendor}
          onChange={(value) => onFilterChange('vendor', value)}
        />
      </div>
    </div>
  )
}

export default DeliveryFilterBar
